// src/components/OrderConfirmation.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Divider,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    // Get order data from location state, passed from checkout component
    if (location.state && location.state.orderData) {
      setOrderData(location.state.orderData);
      setLoading(false);
      
      // Track conversion for Google Ads
      if (window.gtag) {
        try {
          const order = location.state.orderData.order;
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17051406794/rqYRCKrg1sIaEMqj38I_',
            'value': order.total,
            'currency': 'USD',
            'transaction_id': order.paymentId
          });
          console.log('Google Ads conversion tracking sent for order:', order.paymentId);
        } catch (err) {
          console.error('Error tracking Google Ads conversion:', err);
        }
      }
    } else {
      // If no order data is available, redirect to subscriptions
      navigate('/subscriptions');
    }
  }, [location, navigate]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleGoToSubscriptions = () => {
    navigate('/subscriptions');
  };

  if (loading) {
    return (
      <Container 
        sx={{ 
          mt: 6, 
          mb: 6, 
          minHeight: '60vh', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center' 
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  // Extract order data for readability
  const { order, subscriptions } = orderData;
  const { address, cart, total, createdAt, paymentId } = order;

  return (
    <Container maxWidth="md" sx={{ mt: 6, mb: 6 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            mb: 4
          }}
        >
          <CheckCircleOutlineIcon 
            sx={{ 
              fontSize: 80, 
              color: 'success.main',
              mb: 2
            }} 
          />
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Order Confirmed!
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" align="center">
            Thank you for your purchase. Your order has been successfully processed.
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={4}>
          {/* Order Summary Section */}
          <Grid item xs={12} md={7}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <ShoppingBagIcon sx={{ mr: 1 }} /> Order Summary
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Order Date: {formatDate(createdAt)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Order ID: {paymentId.substring(0, 8)}...
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <List disablePadding>
                    {cart.map((item, index) => (
                      <ListItem key={index} sx={{ py: 1, px: 0 }}>
                        <ListItemText 
                          primary={item.name} 
                          secondary={`Quantity: ${item.quantity}`}
                        />
                        <Typography variant="body2">
                          ${item.price.toFixed(2)}
                        </Typography>
                      </ListItem>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <ListItem sx={{ py: 1, px: 0 }}>
                      <ListItemText primary="Total" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        ${total.toFixed(2)}
                      </Typography>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Box>
            
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <LocalShippingIcon sx={{ mr: 1 }} /> Billing Information
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2">
                    {address.addressLine1}
                  </Typography>
                  {address.addressLine2 && (
                    <Typography variant="body2">
                      {address.addressLine2}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {address.city}, {address.state} {address.zip}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Phone: {address.phone}
                  </Typography>
                  {address.businessName && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Business: {address.businessName}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Grid>

          {/* Subscription Access Section */}
          <Grid item xs={12} md={5}>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <AccountCircleIcon sx={{ mr: 1 }} /> Your Subscriptions
              </Typography>
              <Card 
                variant="outlined" 
                sx={{ 
                  backgroundColor: '#f8f9ff',
                  border: '1px solid #e3e8ff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="body1" paragraph>
                    Your subscription(s) have been activated and are ready for use.
                  </Typography>
                  
                  {subscriptions.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Active Subscriptions:
                      </Typography>
                      {subscriptions.map((sub, index) => (
                        <Box key={index} sx={{ mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {sub.product[0]?.name || 'Product'}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Valid until:
                            </Typography>
                            <Typography variant="caption" fontWeight="medium">
                              {formatDate(sub.endDate)}
                            </Typography>
                          </Box>
                          <Chip 
                            label="Active" 
                            size="small" 
                            color="success" 
                            sx={{ mt: 1, height: 24 }} 
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                  
                  <Typography variant="body2" paragraph sx={{ color: 'text.secondary' }}>
                    Get started immediately by accessing your test prep materials and practice exams.
                  </Typography>
                  
                  <Box sx={{ mt: 'auto', pt: 2 }}>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      color="primary"
                      endIcon={<ArrowForwardIcon />}
                      onClick={handleGoToSubscriptions}
                      sx={{ 
                        py: 1.5, 
                        fontWeight: 'bold',
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                          boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                        }
                      }}
                    >
                      Go to My Subscriptions
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default OrderConfirmation;